import {
    formatBrl,
    formatDuration,
    formatReferenceMonth,
    type DayOffRequest,
    type HealthPlanRequest,
    type OvertimeRequest,
    type RaroInvestRequest,
} from '../../../../../shared/dpRequests';
import { COMPENSATION_LABELS, HEALTH_PLAN_ACTION_LABELS } from '../../dpRequests.definitions';
import { formatIsoDate } from '../../utils/formatDayOffDates';
import { AttachmentList } from '../AttachmentList';
import { NoteBlock, RequestFacts } from './RequestFacts';

export function OvertimeSummary({ request }: { request: OvertimeRequest }) {
    return (
        <div className="space-y-4">
            <RequestFacts
                rows={[
                    { label: 'Regime', value: request.contractRegime },
                    { label: 'Projeto', value: request.projectName },
                    { label: 'Data', value: formatIsoDate(request.date) },
                    { label: 'Horário', value: `${request.startTime} às ${request.endTime} (${formatDuration(request.durationMinutes)})` },
                    { label: 'Tipo', value: COMPENSATION_LABELS[request.compensationType] },
                    { label: 'Gestor Raro', value: request.raroManagerEmail },
                    {
                        label: 'Gestor cliente',
                        value: request.internalProject ? 'Projeto interno (sem cliente)' : request.clientManagerEmails.join(', '),
                    },
                    {
                        label: 'Aprovação Raro',
                        value: <AttachmentList attachments={request.attachments.filter(item => item.kind !== 'client_manager')} />,
                    },
                    ...(request.attachments.some(item => item.kind === 'client_manager')
                        ? [{
                              label: 'Aprovação cliente',
                              value: <AttachmentList attachments={request.attachments.filter(item => item.kind === 'client_manager')} />,
                          }]
                        : []),
                ]}
            />
            <NoteBlock title="Motivo" text={request.reason} />
            {request.decision?.note && <NoteBlock title="Nota do DP" text={request.decision.note} />}
        </div>
    );
}

export function RaroInvestSummary({ request }: { request: RaroInvestRequest }) {
    return (
        <div className="space-y-4">
            <RequestFacts
                rows={[
                    { label: 'Competência', value: formatReferenceMonth(request.referenceMonth) },
                    { label: 'Valor', value: formatBrl(request.amount) },
                    { label: 'Comprovante', value: <AttachmentList attachments={request.attachments} /> },
                    ...(request.reimbursement
                        ? [{ label: 'Reembolso', value: `Realizado em ${request.reimbursement.at.toDate().toLocaleDateString('pt-BR')}` }]
                        : []),
                ]}
            />
            {request.note && <NoteBlock title="Observação" text={request.note} />}
            {request.decision?.note && <NoteBlock title="Nota do DP" text={request.decision.note} />}
        </div>
    );
}

export function HealthPlanSummary({ request }: { request: HealthPlanRequest }) {
    const before = request.before;
    const currentCovered = before.dependents.filter(item => item.isCovered).map(item => item.name);
    return (
        <div className="space-y-4">
            <RequestFacts
                rows={[
                    { label: 'Pedido', value: HEALTH_PLAN_ACTION_LABELS[request.action] },
                    ...(request.provider ? [{ label: 'Operadora', value: request.provider }] : []),
                    ...(request.includeHolder ? [{ label: 'Titular', value: request.action === 'leave' ? 'Sai do plano (e os dependentes)' : 'Incluir' }] : []),
                    ...(request.dependentNames.length > 0 ? [{ label: 'Dependentes', value: request.dependentNames.join(', ') }] : []),
                    {
                        label: 'Situação antes',
                        value: before.hasHealthInsurance
                            ? `${before.provider ?? 'Operadora não informada'}${currentCovered.length > 0 ? ` · dependentes: ${currentCovered.join(', ')}` : ''}`
                            : 'Sem plano',
                    },
                    ...(request.attachments.length > 0 ? [{ label: 'Anexos', value: <AttachmentList attachments={request.attachments} /> }] : []),
                    ...(request.decision?.effectiveDate ? [{ label: 'Vale desde', value: formatIsoDate(request.decision.effectiveDate) }] : []),
                ]}
            />
            {request.note && <NoteBlock title="Observação" text={request.note} />}
            {request.decision?.note && <NoteBlock title="Nota do DP" text={request.decision.note} />}
        </div>
    );
}

function formatDecisionDate(value: { toDate?: () => Date } | null | undefined): string {
    const date = typeof value?.toDate === 'function' ? value.toDate() : null;
    return date ? date.toLocaleDateString('pt-BR') : '';
}

function managerDecisionText(request: DayOffRequest): string {
    if (!request.managerDecision) return `${request.managerEmail}: aguardando`;
    const verb = request.status === 'rejected' && request.clientApproval?.status !== 'rejected' ? 'recusou' : 'aprovou';
    const when = formatDecisionDate(request.managerDecision.at);
    return `${request.managerEmail}: ${verb}${when ? ` em ${when}` : ''}`;
}

function clientDecisionText(request: DayOffRequest): string {
    const approval = request.clientApproval;
    const status = approval?.status ?? 'not_required';
    if (status === 'not_required') return 'Projeto sem cliente';
    if (status === 'pending') return 'Aguardando o cliente';
    const when = formatDecisionDate(approval?.at);
    const verb = status === 'approved' ? 'aprovou' : 'recusou';
    return `${approval?.by ?? 'Cliente'}: ${verb}${when ? ` em ${when}` : ''}`;
}

export function ClientApprovalSummary({ request }: { request: DayOffRequest }) {
    return (
        <RequestFacts
            rows={[
                { label: 'Gestor Raro', value: managerDecisionText(request) },
                { label: 'Cliente', value: clientDecisionText(request) },
                ...((request.clientEmails ?? []).length > 0 ? [{ label: 'E-mails', value: (request.clientEmails ?? []).join(', ') }] : []),
                ...(request.clientApproval?.note ? [{ label: 'Motivo do cliente', value: request.clientApproval.note }] : []),
                ...(request.managerDecision?.note ? [{ label: 'Nota do gestor', value: request.managerDecision.note }] : []),
            ]}
        />
    );
}
